import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock3, Home, ShieldCheck } from "lucide-react";
import { AppHeader } from "@/components/site/AppHeader";
import { getManagedHomeOverview, listManagedHomes } from "@/lib/homes.functions";

export const Route = createFileRoute("/_authenticated/home/health")({
  validateSearch: (search: Record<string, unknown>) => ({ homeId: typeof search['homeId'] === "string" ? search['homeId'] : undefined }),
  head: () => ({
    meta: [
      { title: "Home Health — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HomeHealthPage,
});

function HomeHealthPage() {
  const { homeId: requestedHomeId } = Route.useSearch();
  const homes = useQuery({ queryKey: ["managed-homes"], queryFn: () => listManagedHomes() });
  const homeId = homes.data?.find((home) => home.id === requestedHomeId)?.id ?? homes.data?.find((home) => !home.isShared)?.id ?? homes.data?.[0]?.id ?? "";
  const overview = useQuery({
    queryKey: ["managed-home-overview", homeId],
    queryFn: () => getManagedHomeOverview({ data: { homeId } }),
    enabled: Boolean(homeId),
  });

  const data = overview.data?.ok ? overview.data : null;
  const reminders = data?.reminders ?? [];
  const due = reminders.filter((reminder: any) => reminder.due_date <= new Date().toISOString().slice(0, 10));
  const upcoming = data?.upcoming ?? [];
  const completed = data?.completed ?? [];

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/home" className="inline-flex items-center gap-2 text-sm font-bold text-[#66758d]">
          <ArrowLeft className="h-4 w-4" /> My Home
        </Link>

        <section className="mt-5 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-6 text-center shadow-sm">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#fff3cf] text-[#c99522]">
            <ShieldCheck className="h-8 w-8" />
          </span>
          <h1 className="mt-4 text-3xl font-extrabold text-[#061b3a]">
            {homes.isLoading || overview.isLoading ? "Loading Home Health…" : !data ? "Home Health unavailable" : due.length ? "Needs attention" : "No reminders overdue"}
          </h1>
          <p className="mt-2 text-sm leading-6 text-[#66758d]">
            {data ? "Based on the dates, completed work and information recorded for this home. Shields does not guess what your property needs." : "Choose a saved home to see its recorded work and reminders."}
          </p>

          {data ? <div className="mt-6 grid gap-3 text-left">
            <HealthRow
              icon={due.length ? Clock3 : CheckCircle2}
              title={due.length ? `${due.length} reminder${due.length === 1 ? "" : "s"} due` : "No reminders overdue"}
              detail="Based on dates recorded in Shields"
              to="/home/maintenance"
              homeId={homeId}
            />
            <HealthRow
              icon={Clock3}
              title={`${upcoming.length} upcoming service${upcoming.length === 1 ? "" : "s"}`}
              detail="Current and scheduled Shields work"
              to="/home/calendar"
              homeId={homeId}
            />
            <HealthRow
              icon={CheckCircle2}
              title={`${completed.length} completed job${completed.length === 1 ? "" : "s"}`}
              detail="Your recorded service history"
              to="/home/history"
              homeId={homeId}
            />
          </div> : null}
        </section>

        {data ? <section className="mt-6 rounded-[1.75rem] border border-[#ead7a2] bg-[#fffdf8] p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#b78216]">
            Recommended for you
          </p>
          <h2 className="mt-2 text-lg font-extrabold text-[#061b3a]">Keep your home record complete</h2>
          <p className="mt-1 text-sm leading-6 text-[#66758d]">
            Add real service dates, certificates and warranties so Shields can help you stay organised without guessing.
          </p>
          <Link
            to="/home/vault"
            search={{ homeId }}
            className="mt-4 inline-flex min-h-11 items-center rounded-xl bg-[#e1b64f] px-4 text-sm font-extrabold text-[#061b3a]"
          >
            View recommendations <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </section> : null}

        {homes.isError ? (
          <section className="mt-6 rounded-2xl border border-[#e1e8f0] bg-white p-5 text-center">
            <Home className="mx-auto h-7 w-7 text-destructive" />
            <p className="mt-2 text-sm text-[#66758d]">We couldn’t load your properties.</p>
            <button type="button" onClick={() => homes.refetch()} className="btn-secondary-hero mt-4">Try again</button>
          </section>
        ) : !homeId ? (
          <section className="mt-6 rounded-2xl border border-[#e1e8f0] bg-white p-5 text-center">
            <Home className="mx-auto h-7 w-7 text-[#0b3568]" />
            <p className="mt-2 text-sm text-[#66758d]">Add a property in My Home to start building your Home Health record.</p>
            <Link to="/home" className="btn-hero mt-4 inline-flex">Add a property</Link>
          </section>
        ) : overview.isError || (overview.data && !overview.data.ok) ? (
          <section className="mt-6 rounded-2xl border border-[#e1e8f0] bg-white p-5 text-center">
            <ShieldCheck className="mx-auto h-7 w-7 text-destructive" />
            <p className="mt-2 text-sm text-[#66758d]">
              {overview.data && !overview.data.ok ? overview.data.error : "We couldn’t load this Home Health record."}
            </p>
            <button type="button" onClick={() => overview.refetch()} className="btn-secondary-hero mt-4">Try again</button>
          </section>
        ) : null}
      </main>
    </div>
  );
}

function HealthRow({
  icon: Icon,
  title,
  detail,
  to,
  homeId,
}: {
  icon: typeof Home;
  title: string;
  detail: string;
  to: "/home/maintenance" | "/home/calendar" | "/home/history";
  homeId: string;
}) {
  return (
    <Link to={to} search={{ homeId }} className="flex min-h-16 items-center gap-3 rounded-2xl border border-[#e7edf4] p-4">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#ecf8f2] text-[#1f8a5b]">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block text-[#061b3a]">{title}</strong>
        <small className="block text-sm text-[#66758d]">{detail}</small>
      </span>
      <ArrowRight className="h-5 w-5 text-[#66758d]" />
    </Link>
  );
}
